import { useCallback, useMemo, useRef, useState } from "react";
import { useWsSubscription } from "hooks/useWsSubscription";
import type { AddOnStatus, AddOnType } from "pages/media/types";
import {
  ADDON_TYPE_TO_TAB,
  type ContentRead,
  type TabKey,
} from "@libertify/experience-viewer";
import {
  NOTIFICATIONS_EVENT_TYPE,
  type WSEvent,
} from "../../../store/websocketStore";

export interface ReadyToast {
  id: number;
  tabKey: TabKey;
}

export interface UseAddOnStatusesArgs {
  projectId: string;
  /** Enabled add-ons (from GET /projects/{id}/add-ons). */
  enabledAddOns: AddOnType[];
  /** Add-ons whose content already exists (from the loaded add-on assets). */
  readyAddOns: AddOnType[];
  /** Add-ons whose generation ENDED IN ERROR per the project manifest —
   *  without this baseline, an add-on that failed before this mount would
   *  read as `Generating` forever (no WS event replays a past failure). */
  erroredAddOns?: AddOnType[];
  /** Content reads of add-ons whose content is read AFTER their `Generated`
   *  status (Podcast: project -> script -> mp3). A listed add-on reports
   *  `Generating` while its read is `loading`, and its toast / unread dot
   *  show only once the read `renewed` the content. Unlisted add-ons keep
   *  their status as reported. */
  contentReads?: Partial<Record<AddOnType, ContentRead>>;
  /** Called on a genuine transition into Generated so the page re-pulls content. */
  onAddOnReady?: (type: AddOnType) => void;
}

export interface UseAddOnStatusesResult {
  /** Per-tab generation status. */
  statusByTab: Partial<Record<TabKey, AddOnStatus>>;
  /** One entry per tab that just became ready (drives the toasts). */
  readyToasts: ReadyToast[];
  dismissToast: (id: number) => void;
  /**
   * Tabs that genuinely transitioned Generating → Generated during this
   * mount (not tabs already Generated on load) — the eligibility gate for
   * the unread green dot, kept even after its toast is dismissed.
   */
  justReadyTabs: Set<TabKey>;
  /** Live WS statuses per add-on, including tab-less ones (Chatbot). */
  liveStatusByType: Partial<Record<AddOnType, AddOnStatus>>;
}

const GENERATED: AddOnStatus = "Generated";
const GENERATING: AddOnStatus = "Generating";

/**
 * Owns the per-add-on generation status for the experience-v2 Knowledge Hub.
 *
 * Only live `AddOnStatusChanged` WebSocket reports are state; the baseline —
 * enabled add-ons count as `Generating` until their content exists, loaded
 * content counts as `Generated` — is derived at render from the CURRENT
 * `enabledAddOns`/`readyAddOns`. (Both arrive from async fetches, so seeding
 * state in an initializer would capture their empty first-render values
 * forever.)
 *
 * On a genuine transition into `Generated` (replayed events are ignored) it
 * calls `onAddOnReady` so the page re-pulls that add-on's content, and queues
 * one toast per *tab* — once EVERY add-on sharing the tab is ready. Today
 * every tab maps to exactly one add-on (AudioDoc and Podcast got their own
 * tabs in MED-7075), but the "every sibling ready" check stays generic in
 * case a tab is ever shared again. Status is tracked for every add-on; only
 * add-ons that map to a tab surface a status/toast. The same transition also
 * adds the tab to `justReadyTabs`, which the page uses to gate the unread
 * green dot — a tab already Generated on load never qualifies.
 */
export function useAddOnStatuses({
  projectId,
  enabledAddOns,
  readyAddOns,
  erroredAddOns,
  contentReads,
  onAddOnReady,
}: UseAddOnStatusesArgs): UseAddOnStatusesResult {
  const [wsStatusByType, setWsStatusByType] = useState<
    Partial<Record<AddOnType, AddOnStatus>>
  >({});
  // Event-time mirror of wsStatusByType so the handler can detect an actual
  // transition into Generated without depending on the render cycle.
  const wsStatusRef = useRef<Partial<Record<AddOnType, AddOnStatus>>>({});

  const readySet = useMemo(() => new Set(readyAddOns), [readyAddOns]);
  const erroredSet = useMemo(
    () => new Set(erroredAddOns ?? []),
    [erroredAddOns]
  );

  // Tabs whose content read hasn't brought new content: still loading, or it
  // kept the previous content (a failed re-read), which is not "ready".
  const heldBackTabs = useMemo(
    () =>
      new Set(
        (Object.entries(contentReads ?? {}) as [AddOnType, ContentRead][])
          .filter(([, read]) => read !== "renewed")
          .map(([type]) => ADDON_TYPE_TO_TAB[type])
      ),
    [contentReads]
  );

  const [readyToasts, setReadyToasts] = useState<ReadyToast[]>([]);
  const toastIdRef = useRef(0);
  // Tabs already toasted — a replayed event or the shared audio pair toasts
  // once. Not seeded from readyAddOns: the transition guard below already
  // suppresses toasts for content that existed before mount.
  const toastedTabs = useRef(new Set<TabKey>());
  // Reactive twin of toastedTabs — same tabs, but never cleared by dismissing
  // a toast, so the unread green dot stays eligible after the toast is gone.
  const [justReadyTabs, setJustReadyTabs] = useState<Set<TabKey>>(
    () => new Set()
  );

  // Queues the toast + unread-dot eligibility for the tab of a Generated
  // add-on, once EVERY enabled add-on sharing the tab is ready (the event's
  // own type stands in while the enabled fetch is still loading).
  const queueTabReady = useCallback(
    (type: AddOnType) => {
      const tabKey = ADDON_TYPE_TO_TAB[type];
      if (!tabKey || toastedTabs.current.has(tabKey)) return;
      const siblings = enabledAddOns.filter(
        (t) => ADDON_TYPE_TO_TAB[t] === tabKey
      );
      const tabAddOns = siblings.length > 0 ? siblings : [type];
      const allReady = tabAddOns.every(
        (t) =>
          (wsStatusRef.current[t] ??
            (readySet.has(t) ? GENERATED : undefined)) === GENERATED
      );
      if (!allReady) return;

      toastedTabs.current.add(tabKey);
      toastIdRef.current += 1;
      const id = toastIdRef.current;
      setReadyToasts((prev) => [...prev, { id, tabKey }]);
      setJustReadyTabs((prev) =>
        prev.has(tabKey) ? prev : new Set(prev).add(tabKey)
      );
    },
    [enabledAddOns, readySet]
  );

  // `onAddOnReady` is expected to be memoized by the caller so the handler
  // (and thus the WS subscription) stays reasonably stable.
  const handler = useCallback(
    (event: WSEvent) => {
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.AddOnStatusChanged) return;
      if (event.project_id !== projectId) return;
      const type = event.add_on_type as AddOnType | undefined;
      if (!type) return;
      const status = event.status as AddOnStatus;

      const wasGenerated =
        (wsStatusRef.current[type] ??
          (readySet.has(type) ? GENERATED : undefined)) === GENERATED;

      if (wsStatusRef.current[type] !== status) {
        const next = { ...wsStatusRef.current, [type]: status };
        wsStatusRef.current = next;
        setWsStatusByType(next);
      }

      // Only a genuine transition into Generated triggers work — a replayed
      // event for content that is already there must not refetch or toast.
      if (status !== GENERATED || wasGenerated) return;

      onAddOnReady?.(type);

      queueTabReady(type);
    },
    [projectId, readySet, onAddOnReady, queueTabReady]
  );

  useWsSubscription({ type: "project", id: projectId }, handler);

  // Hides a toast and unread dot until their tab's content read renews it.
  const shownToasts = useMemo<ReadyToast[]>(
    () =>
      heldBackTabs.size === 0
        ? readyToasts
        : readyToasts.filter(({ tabKey }) => !heldBackTabs.has(tabKey)),
    [readyToasts, heldBackTabs]
  );
  const shownJustReadyTabs = useMemo<Set<TabKey>>(
    () =>
      heldBackTabs.size === 0
        ? justReadyTabs
        : new Set([...justReadyTabs].filter((tab) => !heldBackTabs.has(tab))),
    [justReadyTabs, heldBackTabs]
  );

  const dismissToast = useCallback((id: number) => {
    setReadyToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Baseline when no live WS report exists for the add-on: loaded content
  // means Generated, a manifest failure means Error, anything else is still
  // in flight.
  const baselineStatus = useCallback(
    (type: AddOnType): AddOnStatus => {
      if (readySet.has(type)) return GENERATED;
      if (erroredSet.has(type)) return "Error";
      return GENERATING;
    },
    [readySet, erroredSet]
  );

  const statusByTab = useMemo<Partial<Record<TabKey, AddOnStatus>>>(() => {
    const byTab: Partial<Record<TabKey, AddOnStatus>> = {};
    // Enabled add-ons plus any the WS has reported (the enabled fetch may
    // still be in flight when the first events arrive).
    const types = new Set<AddOnType>([
      ...enabledAddOns,
      ...(Object.keys(wsStatusByType) as AddOnType[]),
    ]);
    for (const type of types) {
      const tabKey = ADDON_TYPE_TO_TAB[type];
      if (!tabKey) continue;
      const reported = wsStatusByType[type] ?? baselineStatus(type);
      // Generated but its content is still loading → keep the loading state.
      const status =
        reported === GENERATED && contentReads?.[type] === "loading"
          ? GENERATING
          : reported;
      const existing = byTab[tabKey];
      // Error wins, and the tab is Generated only when EVERY add-on mapped
      // to it is — moot today (one add-on per tab) but keeps this correct
      // if a tab is ever shared again.
      if (status === "Error" || existing === "Error") {
        byTab[tabKey] = "Error";
      } else if (!existing || existing === GENERATED) {
        byTab[tabKey] = status;
      }
    }
    return byTab;
  }, [enabledAddOns, baselineStatus, wsStatusByType, contentReads]);

  return {
    statusByTab,
    readyToasts: shownToasts,
    dismissToast,
    justReadyTabs: shownJustReadyTabs,
    liveStatusByType: wsStatusByType,
  };
}
